import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputRightElement,
  InputGroup,
  InputProps as TextInputElementProps,
  Flex,
} from '@chakra-ui/react';
import { MaskitoOptions, maskitoTransform } from '@maskito/core';
import {
  maskitoNumberOptionsGenerator,
  maskitoParseNumber,
} from '@maskito/kit';
import { useMaskito } from '@maskito/react';
import React, { RefCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { RegisterOptions } from 'react-hook-form';

import { NumberPath, StrictFieldValues, StringPath } from './types';

export type MaskTypes =
  | 'usd'
  | 'signedUsd'
  | 'percentage'
  | 'number'
  | 'integer'
  | 'positiveNumber'
  | 'positiveInteger'
  | 'none';

export const USD_MASK_OPTIONS = maskitoNumberOptionsGenerator({
  decimalZeroPadding: true,
  precision: 2,
  thousandSeparator: ',',
  decimalSeparator: '.',
  min: 0,
  prefix: '$',
});

export const SIGNED_USD_MASK_OPTIONS = maskitoNumberOptionsGenerator({
  decimalZeroPadding: true,
  precision: 2,
  thousandSeparator: ',',
  decimalSeparator: '.',
  prefix: '$',
});

export const PERCENTAGE_MASK_OPTIONS = maskitoNumberOptionsGenerator({
  precision: Infinity,
  decimalSeparator: '.',
  min: 0,
  max: 100,
  postfix: '%',
});

export const NUMBER_MASK_OPTIONS = maskitoNumberOptionsGenerator({
  precision: Infinity,
  thousandSeparator: ',',
});

export const INTEGER_MASK_OPTIONS = maskitoNumberOptionsGenerator({
  precision: 0,
  thousandSeparator: ',',
});

export const POSITIVE_NUMBER_MASK_OPTIONS = maskitoNumberOptionsGenerator({
  precision: Infinity,
  min: 0,
  thousandSeparator: ',',
});

export const POSITIVE_INTEGER_MASK_OPTIONS = maskitoNumberOptionsGenerator({
  precision: 0,
  min: 0,
  thousandSeparator: ',',
});

const parseNumber = (val: string) => {
  const number = maskitoParseNumber(val);
  return Number.isNaN(number) ? null : number;
};

const OPTIONS_MAP: Record<
  MaskTypes,
  {
    maskOptions?: MaskitoOptions;
    parser?: (val: string) => number | null; // Transforms HTML input value into react-hook-form state
    formatter?: (val: number | null) => string; // Transforms react-hook-form state into HTML input value
  }
> = {
  usd: {
    maskOptions: USD_MASK_OPTIONS,
    parser: parseNumber,
    formatter: (val) => maskitoTransform(String(val ?? ''), USD_MASK_OPTIONS),
  },
  signedUsd: {
    maskOptions: SIGNED_USD_MASK_OPTIONS,
    parser: parseNumber,
    formatter: (val) =>
      maskitoTransform(String(val ?? ''), SIGNED_USD_MASK_OPTIONS),
  },
  percentage: {
    maskOptions: PERCENTAGE_MASK_OPTIONS,
    parser: (val) => {
      const number = maskitoParseNumber(val);
      return Number.isNaN(number) ? null : number / 100;
    },
    formatter: (val) =>
      val === null
        ? ''
        : maskitoTransform(String(val * 100), PERCENTAGE_MASK_OPTIONS),
  },
  number: {
    maskOptions: NUMBER_MASK_OPTIONS,
    parser: parseNumber,
    formatter: (val) =>
      maskitoTransform(String(val ?? ''), NUMBER_MASK_OPTIONS),
  },
  integer: {
    maskOptions: INTEGER_MASK_OPTIONS,
    parser: parseNumber,
    formatter: (val) =>
      maskitoTransform(String(val ?? ''), INTEGER_MASK_OPTIONS),
  },
  positiveNumber: {
    maskOptions: POSITIVE_NUMBER_MASK_OPTIONS,
    parser: parseNumber,
    formatter: (val) =>
      maskitoTransform(String(val ?? ''), POSITIVE_NUMBER_MASK_OPTIONS),
  },
  positiveInteger: {
    maskOptions: POSITIVE_INTEGER_MASK_OPTIONS,
    parser: parseNumber,
    formatter: (val) =>
      maskitoTransform(String(val ?? ''), POSITIVE_INTEGER_MASK_OPTIONS),
  },
  none: {
    maskOptions: undefined,
  },
};

const REQUIRED_FIELD_RULES = {
  required: { value: true, message: 'This is a required field.' },
};

const TEXT_INPUT_RULES_MAP = {
  usd: {
    ...REQUIRED_FIELD_RULES,
    pattern: {
      value: /^\$\d+/, // This regex prevents react-hook-form from interpreting '$' as a valid value.
      message: 'Enter a valid dollar amount.',
    },
  },
  signedUsd: {
    ...REQUIRED_FIELD_RULES,
    pattern: {
      value: /^\$\d+/, // This regex prevents react-hook-form from interpreting '$' as a valid value.
      message: 'Enter a valid dollar amount.',
    },
  },
  percentage: {
    ...REQUIRED_FIELD_RULES,
    pattern: {
      value: /\d+%$/, // This regex prevents react-hook-form from interpreting '%' as a valid value.
      message: 'Enter a valid percentage.',
    },
  },
  number: REQUIRED_FIELD_RULES,
  integer: REQUIRED_FIELD_RULES,
  positiveNumber: REQUIRED_FIELD_RULES,
  positiveInteger: REQUIRED_FIELD_RULES,
  none: REQUIRED_FIELD_RULES,
};

export type TextInputProps<T extends StrictFieldValues> =
  | {
      name: StringPath<T>;
      label?: string;
      placeholder?: string;
      defaultValue?: string;
      mask?: undefined;
      required?: boolean;
      helperText?: React.ReactNode;
      inputRightElement?: React.ReactNode;
      customRules?: RegisterOptions<T>;
      isReadOnly?: boolean;
      isDisabled?: boolean;
      InputProps?: TextInputElementProps;
      tag?: React.ReactNode;
    }
  | {
      name: NumberPath<T>;
      label?: string;
      placeholder?: string;
      defaultValue?: string;
      mask: MaskTypes;
      required?: boolean;
      helperText?: React.ReactNode;
      inputRightElement?: React.ReactNode;
      customRules?: RegisterOptions<T>;
      isReadOnly?: boolean;
      isDisabled?: boolean;
      InputProps?: TextInputElementProps;
      tag?: React.ReactNode;
    };

/**
 * A flexible text input component with optional input masking for various formats.
 *
 * @template T - The form data type that extends StrictFieldValues
 *
 * Props:
 * - `name`: Field name in the form. Type depends on mask - when mask is undefined, name must be a string field; when mask is provided, name must be a number field
 * - `label`: Optional label displayed above the input
 * - `placeholder`: Optional placeholder text shown when input is empty
 * - `defaultValue`: Optional default value for the field
 * - `mask`: Optional format mask. When provided, the field stores numbers. Possible values: "usd", "signedUsd", "percentage", "number", "integer", "positiveNumber", "positiveInteger"
 * - `required`: When true, field is required and shows validation error if empty
 * - `isReadonly`: Optional, will render input as read-only when true
 * - `isDisabled`: Optional, will disable input when true
 * - `helperText`: Optional helper text displayed below the input
 * - `inputRightElement`: Optional element to display on the right side of the input
 * - `customRules`: Additional validation rules to apply to the field, merged with default rules based on the mask type
 * - `tag`: Optional, can pass a react component to render as a tag next to the input
 *
 * @example
 * // String input (no mask)
 * <TextInput<FormData> name="username" label="Username" required />
 *
 * @example
 * // Number input with USD mask
 * <TextInput<FormData> name="price" label="Price" mask="usd" />
 *
 * @example
 * // Percentage input with helper text
 * <TextInput<FormData>
 *   name="taxRate"
 *   label="Tax Rate"
 *   mask="percentage"
 *   helperText="Enter value as percentage (e.g., 10 for 10%)"
 * />
 */
const TextInputInner = <T extends StrictFieldValues>(
  {
    name,
    label,
    placeholder = '',
    defaultValue,
    mask = 'none',
    required,
    isReadOnly,
    isDisabled,
    helperText = '',
    inputRightElement,
    customRules,
    InputProps,
    tag,
  }: TextInputProps<T>,
  ref?: React.Ref<HTMLInputElement>,
) => {
  const { control } = useFormContext<T>();
  const { maskOptions, formatter, parser } = OPTIONS_MAP[mask];
  const maskitoRef = useMaskito({ options: maskOptions });
  const rules = required ? TEXT_INPUT_RULES_MAP[mask] : undefined;

  return (
    <Controller
      name={name}
      control={control}
      // Can cast to any here because StringPath ensures that the type of this
      // field is always a string.
      defaultValue={defaultValue as any}
      rules={{ ...rules, ...customRules }}
      render={({ field, fieldState: { error } }) => {
        const newRef: RefCallback<HTMLInputElement | null> = (node): void => {
          field.ref(node);
          maskitoRef(node);

          if (typeof ref === 'function') {
            ref(node);
          } else if (ref && typeof ref === 'object' && 'current' in ref) {
            // eslint-disable-next-line no-param-reassign
            (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
          }
        };

        // onChange is left undefined below per the Maskito docs: https://maskito.dev/frameworks/react
        // From the Docs:
        // Maskito core is developed as framework-agnostic library. It does not depend on any JS-framework's peculiarities.
        // It uses only native browser API. That is why you should use native onInput instead of React-specific onChange event.
        // Do not worry, both events works similarly! Read more about it in the official React documentation (https://react.dev/reference/react-dom/components/input#props).

        const maskitoField = {
          ...field,
          ref: newRef,
          // onInput will parse the user's input into react-hook-form state
          onInput: (e: React.FormEvent<HTMLInputElement>) => {
            const value = e.currentTarget.value;
            field.onChange(parser ? parser(value) : value);
          },
          onChange: undefined,
          // value determines what will be shown to the user in the HTML input
          value: (() => {
            const value = field.value;
            if (typeof value === 'string') return value;
            if (typeof value === 'number') return formatter?.(value) ?? '';
            return '';
          })(),
        };

        return (
          <FormControl isInvalid={!!error} onBlur={field.onBlur}>
            <Flex alignItems="center">
              {label && <FormLabel fontWeight="bold">{label}</FormLabel>}
              {tag}
            </Flex>
            <InputGroup>
              <Input
                type="text"
                background="var(--chakra-colors-chakra-body-bg)"
                placeholder={placeholder}
                isReadOnly={isReadOnly}
                isDisabled={isDisabled}
                {...maskitoField}
                {...InputProps}
                data-1p-ignore // disables 1password suggestions
              />

              {inputRightElement && (
                <InputRightElement>{inputRightElement}</InputRightElement>
              )}
            </InputGroup>
            <FormErrorMessage>{error?.message}</FormErrorMessage>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
          </FormControl>
        );
      }}
    />
  );
};

/**
 * A flexible text input component with optional input masking for various formats.
 *
 * @template T - The form data type that extends StrictFieldValues
 *
 * Props:
 * - `name`: Field name in the form. Type depends on mask - when mask is undefined, name must be a string field; when mask is provided, name must be a number field
 * - `label`: Optional label displayed above the input
 * - `placeholder`: Optional placeholder text shown when input is empty
 * - `defaultValue`: Optional default value for the field
 * - `mask`: Optional format mask. When provided, the field stores numbers. Possible values: "usd", "signedUsd", "percentage", "number", "integer", "positiveNumber", "positiveInteger"
 * - `required`: When true, field is required and shows validation error if empty
 * - `helperText`: Optional helper text displayed below the input
 * - `inputRightElement`: Optional element to display on the right side of the input
 * - `customRules`: Additional validation rules to apply to the field, merged with default rules based on the mask type
 *
 * @example
 * // String input (no mask)
 * <TextInput<FormData> name="username" label="Username" required />
 *
 * @example
 * // Number input with USD mask
 * <TextInput<FormData> name="price" label="Price" mask="usd" />
 *
 * @example
 * // Percentage input with helper text
 * <TextInput<FormData>
 *   name="taxRate"
 *   label="Tax Rate"
 *   mask="percentage"
 *   helperText="Enter value as percentage (e.g., 10 for 10%)"
 * />
 */
export const TextInput = React.forwardRef(TextInputInner) as <
  T extends StrictFieldValues
>(
  props: TextInputProps<T> & { ref?: React.Ref<HTMLInputElement> },
) => ReturnType<typeof TextInputInner>;
